import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Check, ExternalLink, Pencil, X } from 'lucide-react';
import {
  usePlanChildDataUpdate,
  useProcessChildDataUpdate,
  useUpdateChangeRequestReview,
} from '@/hooks/useChildDataUpdateRequest';
import { usePlanCareDay } from '@/hooks/useCareDayRequest';
import { usePlanPause } from '@/hooks/usePauseRequest';
import { useTransition } from '@/hooks/useWorkflows';
import { useGroup, useGroups } from '@/hooks/useGroups';
import { useApprovalPreview } from '@/hooks/useApprovalPreview';
import { useResolveServiceName } from '@/hooks/useResolveServiceName';
import { ApprovalOutcomePanel } from '@/components/workflows/ApprovalOutcomePanel';
import { approveActionLabel, basicChangeText, previewLineText } from '@/components/workflows/approvalPreviewText';
import type { I18n } from '@lingui/core';
import { useContractVersions } from '@/hooks/useContractVersions';
import { ContractLink } from '@/components/shared/entity-links';
import { TransitionDialog } from '@/components/workflows/TransitionDialog';
import { ChildDataUpdateRequestForm } from '@/components/children/ChildDataUpdateRequestForm';
import { CareDayBillingDelta } from '@/components/workflows/CareDayBillingDelta';
import { PauseBillingDelta } from '@/components/workflows/PauseBillingDelta';
import { useLingui } from '@lingui/react';
import { useOptionalAuthContext } from '@/contexts/AuthContext';
import { getRelationshipTypeLabel } from '@/lib/relationship/utils';
import type { RelationshipType } from '@/types/childContactRelationship';
import { EmployeeDataDiff } from '@/components/workflows/EmployeeDataDiff';
import { TimeCorrectionDiff } from '@/components/workflows/TimeCorrectionDiff';
import {
  childContactLines,
  contactDataLines,
  contractLines,
  contractStatusLabel,
  formatRequestDate,
  groupAllocationLines,
  summarizeContractGroup,
} from '@/components/workflows/changeRequestParentSummaries';
import {
  buildChildDataUpdateEditBody,
  changeRequestPayloadToFormValues,
  formatConfidence,
  getContactPrivacySubTypeLabel,
  getContractKindLabel,
  getCommentFieldLabel,
  getEmployeeDataSectionLabel,
  getTimeCorrectionModeLabel,
  isEmployeeFiledFamily,
  getFamilyLabel,
  getMedicalKindLabel,
  getOperationLabel,
  getSourceLabel,
  getSubTypeLabel,
  changeRequestChildIds,
  isChangeRequestTerminalState,
  isSensitiveSubType,
  MAX_CHANGE_REQUEST_CHILD_CARDS,
  parseChangeRequestPayload,
  type ChangeRequestFormValues,
  type ChangeRequestSourceKind,
} from '@/types/changeRequest';
import type {
  change_requests_ChildDataUpdatePlanResponse,
  change_requests_ChildDataUpdateProcessResponse,
  change_requests_CareDayPlanResponse,
  change_requests_PausePlanResponse,
} from '@/api';

interface I18nLike {
  _: (d: { id: string; message: string; values?: Record<string, unknown> }) => string;
}

// Parent-app families (#1715) whose Proposed change renders as labelled lines.
const PARENT_LINE_FAMILIES: ReadonlySet<string> = new Set([
  'contact_data',
  'contract',
  'group_allocation',
  'child_contact',
]);

// Families that name no target child list in `executor.body.child_ids`, so the
// "Target children" row would only ever read "Multiple children (0)".
const NO_TARGET_CHILDREN_FAMILIES: ReadonlySet<string> = new Set([
  'care_day',
  'pause',
  'contact_data',
  'contract',
  'group_allocation',
  'child_contact',
  'employee_data',
  'time_correction',
]);

// A sub_type type label: contract names its kind, employee_data its section.
function typeLabel(family: string | undefined, subType: string, i18n: I18nLike): string {
  switch (family) {
    case 'contract':
      return getContractKindLabel(i18n, subType);
    case 'employee_data':
      return getEmployeeDataSectionLabel(i18n, subType);
    case 'time_correction':
      return getTimeCorrectionModeLabel(i18n, subType);
    case 'contact_data':
      return getContactPrivacySubTypeLabel(i18n, subType) || subType;
    default:
      return getSubTypeLabel(i18n, subType);
  }
}

function parentFamilyLines(
  family: string,
  subType: string,
  body: Record<string, unknown> | undefined,
  i18n: I18nLike,
  currentGroupName: string,
  relationshipLabel: (type: string) => string,
  serviceLabel: (code: string) => string,
): string[] {
  switch (family) {
    case 'contact_data':
      return contactDataLines(body, i18n, subType);
    case 'contract':
      return contractLines(body, i18n, serviceLabel);
    case 'group_allocation':
      return groupAllocationLines(body, i18n, currentGroupName || undefined);
    case 'child_contact':
      return childContactLines(body, i18n, relationshipLabel);
    default:
      return [];
  }
}

// Identifies the contract a parent `contract` request refers to: start date and
// status of the contract group, linked to the version in effect. Its own
// component so the versions query only runs for that family.
function ContractGroupRow({ contractGroupId, i18n }: { contractGroupId: string; i18n: I18nLike }) {
  const versions = useContractVersions(contractGroupId);
  const summary = summarizeContractGroup(versions.data);
  let content: React.ReactNode;
  if (versions.isLoading) {
    content = <span className="text-muted-foreground">…</span>;
  } else if (!summary) {
    content = i18n._({ id: 'changeRequest.detail.unknownContract', message: 'Unknown contract' });
  } else {
    const startLabel = summary.startDate
      ? i18n._({
          id: 'changeRequest.detail.contractStart',
          message: 'Start {date}',
          values: { date: formatRequestDate(summary.startDate) },
        })
      : undefined;
    content = (
      <span className="inline-flex flex-wrap items-center gap-2">
        <ContractLink contractId={summary.versionId} fallbackLabel={startLabel} />
        {summary.status && <Badge variant="outline">{contractStatusLabel(summary.status, i18n)}</Badge>}
      </span>
    );
  }
  return (
    <Row label={i18n._({ id: 'changeRequest.detail.referencedContract', message: 'Contract concerned' })}>
      <div className="text-sm">{content}</div>
    </Row>
  );
}

interface ChangeRequestPayloadProps {
  payload: Record<string, unknown>;
  i18n: I18nLike;
  itemId?: string;
  currentState?: string;
  /**
   * The FSM actions the API currently offers for this item. When provided, every
   * triage button is gated on the exact action key so a transition the server has
   * already removed (concurrent update / rule change) can't be shown. Undefined →
   * fall back to the local state invariants (used by unit tests / legacy callers).
   */
  availableActions?: string[];
  /**
   * Opens another workflow item in the sheet (e.g. the termination case a parent
   * termination request opened). Without it the link is not shown.
   */
  onOpenItem?: (itemId: string) => void;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// changeSummary renders the proposed change for the reviewer. Comment fields are
// humanized; the contact is shown by resolved name (falling back to its ID) and
// the grant/revoke verb is localized.
function changeSummary(
  body: Record<string, unknown> | undefined,
  i18n: I18nLike,
  contactName: string,
  linguiI18n: I18n,
): string {
  if (!body) return '';
  const b = body as {
    comment?: { field?: string; value?: string };
    allergy?: { operation?: string; name?: string; allergy_id?: string };
    medical?: { kind?: string; operation?: string; name?: string; id?: string };
    contact?: { contact_id?: string; grant?: boolean };
    basic?: Record<string, unknown>;
  };
  if (b.basic) return basicChangeText(b.basic, linguiI18n);
  if (b.comment) return `${getCommentFieldLabel(i18n, b.comment.field ?? 'notes')}: ${b.comment.value ?? ''}`;
  if (b.allergy) {
    const who = b.allergy.name || b.allergy.allergy_id || '';
    // Localized verb + noun + the (proper-noun) target, like the comment/contact
    // branches — no raw English tokens.
    return [getOperationLabel(i18n, b.allergy.operation), getSubTypeLabel(i18n, 'allergy'), who]
      .filter(Boolean)
      .join(' ');
  }
  if (b.medical) {
    const who = b.medical.name || b.medical.id || '';
    return [getOperationLabel(i18n, b.medical.operation), getMedicalKindLabel(i18n, b.medical.kind), who]
      .filter(Boolean)
      .join(' ');
  }
  if (b.contact) {
    const verb = b.contact.grant
      ? i18n._({ id: 'changeRequest.verb.grant', message: 'Grant' })
      : i18n._({ id: 'changeRequest.verb.revoke', message: 'Revoke' });
    const who = contactName || b.contact.contact_id || '';
    return `${verb} — ${who}`.trim();
  }
  return '';
}

// Map the canonical flat form values to the in-place review-edit PATCH body
// (ChangeRequestInputDTO shape). Mirrors the create-path nesting in the form.
function reviewEditBodyFromValues(values: ChangeRequestFormValues) {
  return buildChildDataUpdateEditBody({
    subType: values.subType,
    childIds: values.childIds,
    comment:
      values.subType === 'comment'
        ? { field: values.commentField || 'notes', value: values.commentValue }
        : undefined,
    allergy:
      values.subType === 'allergy'
        ? {
            operation: values.allergyOperation,
            allergy_id: values.allergyId || undefined,
            name: values.allergyName || undefined,
            severity: values.allergySeverity || undefined,
            comment: values.allergyComment || undefined,
          }
        : undefined,
    medical:
      values.subType === 'medical'
        ? {
            kind: values.medicalKind,
            operation: values.medicalOperation,
            id: values.medicalId || undefined,
            name: values.medicalName || undefined,
            type: values.medicalType || undefined,
            dosage: values.medicalDosage || undefined,
            comment: values.medicalComment || undefined,
          }
        : undefined,
    contact:
      values.subType === 'contact_pickup' || values.subType === 'contact_emergency'
        ? { contact_id: values.contactId, grant: values.contactGrant }
        : undefined,
  });
}

export function ChangeRequestPayload({ payload, i18n, itemId, currentState, availableActions, onOpenItem }: ChangeRequestPayloadProps) {
  const parsed = parseChangeRequestPayload(payload);
  // The relationship labels take the full Lingui instance, not the I18nLike prop.
  const { i18n: linguiI18n } = useLingui();
  // Parent-app provenance (#1715) + the group_allocation current group name. The
  // lookup runs unconditionally; an empty id keeps the query disabled. The
  // requester and child cards head the sheet (ChangeRequestSubjectCards).
  const requestedByContactId = parsed?.extracted?.requested_by_contact_id ?? '';
  // An admin who filed this request while viewing as the parent may approve it
  // themselves (#2171); the approve dialog says so. Presentation only.
  const userId = useOptionalAuthContext()?.userId;
  const filedByApprover =
    !!userId && requestedByContactId !== '' && (parsed?.extracted?.impersonated_by_user_id ?? '') === userId;
  const rawBody = parsed?.executor?.body as Record<string, unknown> | undefined;
  const currentGroupId =
    parsed?.family === 'group_allocation' && typeof rawBody?.current_group_id === 'string'
      ? rawBody.current_group_id
      : '';
  const contractGroupId =
    parsed?.family === 'contract' && typeof rawBody?.contract_group_id === 'string'
      ? rawBody.contract_group_id
      : '';
  const currentGroup = useGroup(currentGroupId);
  const process = useProcessChildDataUpdate();
  const plan = usePlanChildDataUpdate();
  const careDayPlan = usePlanCareDay();
  const pausePlan = usePlanPause();
  const transition = useTransition();
  const updateReview = useUpdateChangeRequestReview();
  const planMutate = plan.mutate;
  const careDayPlanMutate = careDayPlan.mutate;
  const pausePlanMutate = pausePlan.mutate;
  const [outcomes, setOutcomes] = useState<change_requests_ChildDataUpdateProcessResponse | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);

  // What one click on Approve does: a server dry run, shown above the buttons
  // and repeated in the confirm dialog. Only a pending item can be approved.
  const resolveServiceName = useResolveServiceName();
  const serviceLabel = (code: string) => resolveServiceName(code);
  const relationshipLabel = (type: string) => getRelationshipTypeLabel(type as RelationshipType, linguiI18n);
  const approvalPreview = useApprovalPreview(itemId, currentState === 'pending' && !!parsed?.family);
  const previewData = approvalPreview.data?.supported ? approvalPreview.data : undefined;
  // Group change: the reviewer confirms the target group (pre-selected from the
  // requested name). The list is cached, so loading it for every sheet is cheap.
  const groupsQuery = useGroups(500, 0, parsed?.family === 'group_allocation' && currentState === 'pending');

  // Resolve target child names + the contact name server-side (read-only Plan) so
  // the reviewer sees names rather than opaque IDs. For child_data_update the Plan
  // is trivial/no-op (no billing impact) — it serves only as the name-resolution
  // preview here; richer billing previews arrive when care_day/pause land.
  useEffect(() => {
    if (!itemId) return;
    // The Plan preview is family-specific and only applies to a handled family in
    // a live state. Skip it for manual / unhandled-family / terminal items, which
    // would otherwise error and pop a spurious "Failed to load the change preview"
    // toast on every open. `children` fetches the name-resolution preview;
    // `care_day` fetches the billing-delta preview (#783).
    if (currentState === 'manual' || isChangeRequestTerminalState(currentState)) return;
    if (parsed?.family === 'children') {
      planMutate(itemId);
    } else if (parsed?.family === 'care_day') {
      careDayPlanMutate(itemId);
    } else if (parsed?.family === 'pause') {
      pausePlanMutate(itemId);
    }
  }, [itemId, planMutate, careDayPlanMutate, pausePlanMutate, parsed?.family, currentState]);
  const planData: change_requests_ChildDataUpdatePlanResponse | undefined = plan.data;
  const careDayPlanData: change_requests_CareDayPlanResponse | undefined = careDayPlan.data;
  const pausePlanData: change_requests_PausePlanResponse | undefined = pausePlan.data;

  if (!parsed) {
    return <p className="text-sm text-muted-foreground">{i18n._({ id: 'changeRequest.detail.unavailable', message: 'No change request details.' })}</p>;
  }

  const subType = parsed.sub_type ?? '';
  const sensitive = isSensitiveSubType(subType);
  const body = parsed.executor?.body as Record<string, unknown> | undefined;
  const childIDs = Array.isArray(body?.child_ids) ? (body?.child_ids as string[]) : [];
  const cardChildCount = changeRequestChildIds(parsed).length;
  const childCardsShown = cardChildCount > 0 && cardChildCount <= MAX_CHANGE_REQUEST_CHILD_CARDS;
  const result = parsed.executor?.result;

  // Triage actions are state-gated. A pending item can be approved (one transition,
  // the router cascades execute), edited in place, or rejected. A manual item (the
  // server already offers resolve/reject in available_actions) can be dispositioned.
  // An item stranded in approved (post-transition execute failed) exposes the legacy
  // process-backed apply as a recovery action only — never the triage approve path.
  const isPending = currentState === 'pending';
  const isManual = currentState === 'manual';
  const isChildrenFamily = parsed.family === 'children';
  // employee_data (#1766): read-only server-snapshotted diff (the server refuses
  // edits), and a reject must carry a reason the employee sees.
  const isEmployeeData = parsed.family === 'employee_data';
  // time_correction (#1979): the same rules, its own before-and-after view.
  const isTimeCorrection = parsed.family === 'time_correction';
  const isEmployeeFiled = isEmployeeFiledFamily(parsed.family);
  // The recovery "Retry apply" path runs the children-only process endpoint
  // (useProcessChildDataUpdate) with childIDs, so it is restricted to the `children`
  // family — a care_day / pause item stranded in `approved` must not surface it (its
  // childIDs are empty and the children endpoint is the wrong executor).
  const canRetryApply = currentState === 'approved' && !!itemId && isChildrenFamily;

  // Gate each triage button on the exact FSM action the API offers. When
  // availableActions is undefined (unit tests / legacy callers) fall back to the
  // local state invariant so behavior is unchanged. The in-place Edit path is
  // additionally restricted to the `children` family — ChildDataUpdateRequestForm
  // only produces a children payload, so editing a non-children item here would
  // persist an incompatible shape.
  const actionOffered = (key: string, fallback: boolean) =>
    availableActions ? availableActions.includes(key) : fallback;
  const canApprove = isPending && !!itemId && actionOffered('approve', true);
  // The review form cannot produce a master-data (`basic`) body, so that sub-type is approved or rejected as sent.
  const canEdit = isPending && !!itemId && isChildrenFamily && subType !== 'basic' && actionOffered('approve', true);
  const canRejectPending = isPending && !!itemId && actionOffered('reject', true);
  const canResolve = isManual && !!itemId && actionOffered('resolve', true);
  const canRejectManual = isManual && !!itemId && actionOffered('reject', true);

  const confidence = parsed.resolved?.confidence;
  const candidates = parsed.resolved?.candidates ?? [];
  const sourceKind = parsed.source?.kind;
  const rawExcerpt = parsed.source?.raw_excerpt;

  const editPrefill = changeRequestPayloadToFormValues(parsed);
  const editSource: { kind: ChangeRequestSourceKind; message_id?: string } = {
    kind: (parsed.source?.kind as ChangeRequestSourceKind) || 'web_admin',
    message_id: parsed.source?.message_id,
  };
  // Seed the target picker with resolved names so the edit form shows names, not IDs.
  const initialChildOptions = (planData?.targets ?? [])
    .filter((t) => !!t.child_id)
    .map((t) => ({ value: t.child_id as string, label: t.name || (t.child_id as string) }));

  const targetNameById = new Map(
    (planData?.targets ?? []).filter((t) => !!t.child_id && !!t.name).map((t) => [t.child_id as string, t.name as string]),
  );
  const targetNames = (planData?.targets ?? [])
    .map((t) => t.name || t.child_id || '')
    .filter(Boolean);
  const contactName = planData?.change?.contact_name ?? '';

  const isParentLineFamily = PARENT_LINE_FAMILIES.has(parsed.family ?? '');
  const parentLines = isParentLineFamily
    ? parentFamilyLines(parsed.family ?? '', subType, body, i18n, currentGroup.data?.name ?? '', relationshipLabel, serviceLabel)
    : [];

  const previewBlocked = (previewData?.blockers.length ?? 0) > 0;
  const approveLabel = approveActionLabel(
    linguiI18n,
    parsed.family ?? '',
    subType,
    (previewData?.actions ?? []).map((line) => line.code),
  );
  const textContext = { serviceLabel, relationshipLabel };
  const approveSummary = previewData ? (
    <ul className="list-disc space-y-1 pl-5 text-sm">
      {[...previewData.actions, ...previewData.warnings].map((line, idx) => (
        <li key={`${line.code}-${idx}`}>{previewLineText(line, { i18n: linguiI18n, ...textContext })}</li>
      ))}
    </ul>
  ) : undefined;

  const isGroupChange = parsed.family === 'group_allocation' && isPending && !!itemId;
  const chosenGroupId =
    (typeof body?.target_group_id === 'string' ? body.target_group_id : '') ||
    previewData?.choices?.target_group_id ||
    '';
  // Saved on the request at once, so the preview re-runs for the chosen group.
  // A failed save is reported by the hook's own error toast.
  const handleGroupChoice = (groupId: string) => {
    if (!itemId || !body) return;
    updateReview.mutate({
      id: itemId,
      body: { family: 'group_allocation', resolved: {}, executor: { body: { ...body, target_group_id: groupId } } },
    });
  };
  const groupChoice = isGroupChange
    ? {
        options: (groupsQuery.data ?? [])
          .filter((g) => !!g.id && g.status === 'ACTIVE')
          .map((g) => ({ id: g.id as string, name: g.name ?? '' })),
        value: chosenGroupId,
        onChange: handleGroupChoice,
        saving: updateReview.isPending,
      }
    : undefined;

  // A finished request that opened a follow-up case (the termination) links to it.
  const followUpId =
    result?.reference_category === 'contract_termination' && result.reference_id ? result.reference_id : '';

  // Edit-in-place then approve. When the reviewer changed something, PATCH the item
  // (stamps the server-authoritative review.edited marker → outcome `edited`), then
  // approve via the generic transition (router cascades execute). An unchanged
  // submit just approves (outcome `approved_as_is`). NO standalone process call.
  const handleReviewSubmit = async (values: ChangeRequestFormValues, wasEdited: boolean) => {
    if (!itemId) return;
    if (wasEdited) {
      await updateReview.mutateAsync({
        id: itemId,
        body: reviewEditBodyFromValues(values),
        childIds: values.childIds,
      });
    }
    await transition.mutateAsync({ id: itemId, action: 'approve' });
    setShowEdit(false);
  };

  // Resolves only on success, so the confirm stays open (and retryable) on a failure.
  const handleRetryApply = async () => {
    if (!itemId) return;
    setOutcomes(await process.mutateAsync({ id: itemId, childIDs }));
  };

  return (
    <div className="space-y-4">
      <Row label={i18n._({ id: 'changeRequest.detail.family', message: 'Family' })}>
        <p className="text-sm">{getFamilyLabel(i18n, parsed.family ?? '')}</p>
      </Row>

      {/* contact_data and group_allocation carry no sub_type (#1715); the family
          row already names them, so the type row is omitted. */}
      {subType && parsed.family !== 'child_contact' ? (
        <Row label={i18n._({ id: 'changeRequest.detail.subType', message: 'Change type' })}>
          <div className="flex items-center gap-2">
            <span className="text-sm">{typeLabel(parsed.family, subType, i18n)}</span>
            {/* The sensitivity badge describes the children sub_types; an
                employee_data section is always reviewed, never auto-applied. */}
            {isEmployeeFiled ? null : sensitive ? (
              <Badge variant="destructive" aria-label={i18n._({ id: 'changeRequest.badge.requiresApproval', message: 'Requires approval' })}>
                {i18n._({ id: 'changeRequest.badge.requiresApproval', message: 'Requires approval' })}
              </Badge>
            ) : (
              <Badge variant="secondary">
                {i18n._({ id: 'changeRequest.badge.benign', message: 'Benign' })}
              </Badge>
            )}
          </div>
        </Row>
      ) : null}

      <Row label={i18n._({ id: 'changeRequest.triage.source.label', message: 'Source' })}>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{getSourceLabel(i18n, sourceKind)}</Badge>
          <span
            className="text-sm text-muted-foreground"
            aria-label={i18n._({ id: 'changeRequest.triage.confidenceLabel', message: 'Confidence' })}
            title={i18n._({ id: 'changeRequest.triage.confidenceLabel', message: 'Confidence' })}
          >
            {formatConfidence(confidence)}
          </span>
        </div>
      </Row>

      {contractGroupId && <ContractGroupRow contractGroupId={contractGroupId} i18n={i18n} />}

      {rawExcerpt && (
        <Row label={i18n._({ id: 'changeRequest.triage.sourceExcerpt', message: 'Source excerpt' })}>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-2">
            {rawExcerpt}
          </p>
        </Row>
      )}

      {/* care_day / pause target a contract group, not children — hide the child
          target row (their billing-delta preview identifies the contract group).
          contact_data / contract / group_allocation (#1715) carry no child_ids
          list either. Up to MAX_CHANGE_REQUEST_CHILD_CARDS children already head
          the sheet as cards, so the row stays only for a larger bulk request. */}
      {!NO_TARGET_CHILDREN_FAMILIES.has(parsed.family ?? '') && !childCardsShown && (
        <Row label={i18n._({ id: 'changeRequest.detail.targets', message: 'Target children' })}>
          <p className="text-sm">
            {targetNames.length > 0
              ? targetNames.join(', ')
              : i18n._({ id: 'changeRequest.detail.bulkCount', message: 'Multiple children' })}{' '}
            ({childIDs.length})
          </p>
        </Row>
      )}

      {candidates.length > 0 && (
        <Row label={i18n._({ id: 'changeRequest.triage.candidates', message: 'Resolution candidates' })}>
          <ul className="text-sm space-y-1">
            {candidates.map((cand, idx) => (
              <li key={`${cand.child_id ?? cand.contract_group_id ?? 'cand'}-${idx}`} className="flex items-center gap-2">
                <span>{cand.label || cand.child_id || cand.contract_group_id || ''}</span>
                <span className="text-muted-foreground">{formatConfidence(cand.confidence)}</span>
              </li>
            ))}
          </ul>
          {candidates.length > 1 && (
            <p className="mt-1 text-xs text-warning-on-surface">
              {i18n._({
                id: 'changeRequest.triage.candidatesAmbiguous',
                message: 'Ambiguous target — pick the right child by editing before approving.',
              })}
            </p>
          )}
        </Row>
      )}

      {/* For care_day / pause the billing-delta Plan preview below IS the
          proposed-change view; the children changeSummary is the name-resolution
          preview. */}
      {parsed.family !== 'care_day' && parsed.family !== 'pause' && (
        <Row label={i18n._({ id: 'changeRequest.detail.change', message: 'Proposed change' })}>
          {isEmployeeData ? (
            <EmployeeDataDiff body={body} i18n={i18n} />
          ) : isTimeCorrection ? (
            <TimeCorrectionDiff body={body} i18n={linguiI18n} />
          ) : isParentLineFamily ? (
            <div className="space-y-1">
              {parentLines.map((text, idx) => (
                <p key={`${idx}-${text}`} className="text-sm break-words">
                  {text}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-sm break-words">{changeSummary(body, i18n, contactName, linguiI18n)}</p>
          )}
        </Row>
      )}

      {/* care_day billing-delta preview (#738 surface): the bounded window, the
          weekday change, current→proposed days, and the affected invoices — so the
          admin approves knowingly. Edit-in-place stays children-only (below): a
          care_day item is not editable via ChildDataUpdateRequestForm. */}
      {parsed.family === 'care_day' && careDayPlanData && (
        <Row label={i18n._({ id: 'careDay.plan.billingDelta', message: 'Billing impact preview' })}>
          <CareDayBillingDelta plan={careDayPlanData} i18n={i18n} />
        </Row>
      )}

      {/* pause billing-delta preview (#784): the bounded window, the create/extend/
          cancel sub-type, and the affected invoices — so the admin approves
          knowingly. Read-only; a pause item is not editable via the children form. */}
      {parsed.family === 'pause' && pausePlanData && (
        <Row label={i18n._({ id: 'pause.plan.billingDelta', message: 'Billing impact preview' })}>
          <PauseBillingDelta plan={pausePlanData} i18n={i18n} />
        </Row>
      )}

      {result?.status && (
        <Row label={i18n._({ id: 'changeRequest.detail.result', message: 'Result' })}>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant={result.status === 'ok' ? 'secondary' : result.status === 'partial' ? 'outline' : 'destructive'}>
                {result.status === 'ok'
                  ? i18n._({ id: 'changeRequest.result.done', message: 'Done' })
                  : result.status === 'partial'
                    ? i18n._({ id: 'changeRequest.result.partial', message: 'Partly done' })
                    : i18n._({ id: 'changeRequest.result.failed', message: 'Failed' })}
              </Badge>
              {result.detail && <span className="text-sm text-muted-foreground">{result.detail}</span>}
            </div>
            {followUpId && onOpenItem && (
              <Button variant="outline" size="sm" onClick={() => onOpenItem(followUpId)} data-testid="open-follow-up">
                <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
                {i18n._({ id: 'changeRequest.result.openTermination', message: 'Open the termination case' })}
              </Button>
            )}
          </div>
        </Row>
      )}

      {/* What one click on Approve will do (server dry run). Hidden for families
          without a preview; their requested values above are the whole story. */}
      {isPending && itemId && (approvalPreview.isLoading || approvalPreview.isError || previewData || groupChoice) && (
        <ApprovalOutcomePanel
          preview={previewData}
          isLoading={approvalPreview.isLoading}
          isError={approvalPreview.isError}
          textContext={textContext}
          groupChoice={groupChoice}
        />
      )}

      {/* A manual item is a message: nothing is applied automatically. */}
      {isManual && (
        <p className="text-sm text-muted-foreground" data-testid="manual-hint">
          {i18n._({
            id: 'changeRequest.manual.hint',
            message: 'This request is a message: the system cannot apply it by itself. Do what it asks, then press "Mark resolved".',
          })}
        </p>
      )}

      {outcomes?.outcomes && outcomes.outcomes.length > 0 && (
        <Row label={i18n._({ id: 'changeRequest.detail.perChild', message: 'Per-child outcomes' })}>
          <ul className="text-sm space-y-1">
            {outcomes.outcomes.map((o, idx) => (
              <li key={`${o.child_id}-${idx}`} className="flex items-center gap-2">
                <StatusBadge
                  tone={o.status === 'ok' ? 'success' : 'danger'}
                  label={
                    o.status === 'ok'
                      ? i18n._({ id: 'changeRequest.outcome.applied', message: 'Applied' })
                      : i18n._({ id: 'changeRequest.outcome.notApplied', message: 'Not applied' })
                  }
                />
                <span className="text-muted-foreground">
                  {targetNameById.get(o.child_id ?? '') ??
                    i18n._({ id: 'changeRequest.outcome.unknownChild', message: 'Child' })}
                </span>
                {o.detail && <span className="text-muted-foreground">— {o.detail}</span>}
              </li>
            ))}
          </ul>
        </Row>
      )}

      {/* Pending triage actions: Approve (one transition, router cascades execute),
          Edit (in-place PATCH + approve, children family only), Reject. Each button
          is gated on the exact FSM action the API offers. */}
      {(canApprove || canEdit || canRejectPending) && (
        <div className="flex flex-wrap gap-2">
          {canApprove && (
            <Button
              onClick={() => setApproveOpen(true)}
              disabled={transition.isPending || updateReview.isPending || approvalPreview.isLoading || previewBlocked}
              data-testid="approve-button"
            >
              <Check className="h-4 w-4 mr-2" aria-hidden="true" />
              {approveLabel}
            </Button>
          )}
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => setShowEdit(true)}
              disabled={transition.isPending || updateReview.isPending}
            >
              <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
              {i18n._({ id: 'changeRequest.triage.edit', message: 'Edit' })}
            </Button>
          )}
          {canRejectPending && (
            <Button
              variant="destructive"
              onClick={() => setRejectOpen(true)}
              disabled={transition.isPending || updateReview.isPending}
            >
              <X className="h-4 w-4 mr-2" aria-hidden="true" />
              {i18n._({ id: 'changeRequest.triage.reject', message: 'Reject' })}
            </Button>
          )}
        </div>
      )}

      {/* Manual disposition: gated on the resolve/reject actions the API offers. */}
      {(canResolve || canRejectManual) && (
        <div className="flex flex-wrap gap-2">
          {canResolve && (
            <Button onClick={() => setResolveOpen(true)}>
              {i18n._({ id: 'changeRequest.triage.markResolved', message: 'Mark resolved' })}
            </Button>
          )}
          {canRejectManual && (
            <Button variant="destructive" onClick={() => setRejectOpen(true)}>
              <X className="h-4 w-4 mr-2" aria-hidden="true" />
              {i18n._({ id: 'changeRequest.triage.reject', message: 'Reject' })}
            </Button>
          )}
        </div>
      )}

      {/* In-place edit: the SAME canonical form, mode=review, submitted via
          onReviewSubmit (PATCH + approve) — never a create-and-reject. Mounted only
          for the `children` family, whose payload the form can actually produce. */}
      {isPending && isChildrenFamily && (
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent size="lg">
            <DialogHeader>
              <DialogTitle>
                {i18n._({ id: 'changeRequest.dialog.reviewEditTitle', message: 'Edit & approve change request' })}
              </DialogTitle>
              <DialogDescription>
                {i18n._({
                  id: 'changeRequest.dialog.reviewEditDescription',
                  message: 'Correct any details, then save to apply. Unchanged fields approve as-is.',
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <ChildDataUpdateRequestForm
                mode="review"
                prefill={editPrefill}
                initialChildOptions={initialChildOptions}
                source={editSource}
                onReviewSubmit={handleReviewSubmit}
                onCancel={() => setShowEdit(false)}
              />
            </DialogBody>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirmation dialogs for the transition actions. */}
      {itemId && (
        <>
          <TransitionDialog
            open={approveOpen}
            onOpenChange={setApproveOpen}
            itemId={itemId}
            action="approve"
            currentState={currentState ?? ''}
            title={approveLabel}
            summary={approveSummary}
            confirmLabel={approveLabel}
            notice={
              filedByApprover
                ? i18n._({
                    id: 'changeRequest.confirm.filedByYou',
                    message: 'You entered this request for the parent.',
                  })
                : undefined
            }
          />
          <TransitionDialog
            open={rejectOpen}
            onOpenChange={setRejectOpen}
            itemId={itemId}
            action="reject"
            currentState={currentState ?? ''}
            // Only a parent-app request has a requester who can read the reason (#1716);
            // an employee_data (#1766) or time_correction (#1979) request requires
            // one for the employee.
            parentNoteEnabled={requestedByContactId !== '' || isEmployeeFiled}
            parentNoteRequired={isEmployeeFiled}
          />
          <TransitionDialog
            open={resolveOpen}
            onOpenChange={setResolveOpen}
            itemId={itemId}
            action="resolve"
            currentState={currentState ?? ''}
          />
        </>
      )}

      {/* Recovery only: an item stranded in `approved` (post-transition execute
          failed) can re-run the executor. This is NOT the triage approve path. */}
      {canRetryApply && (
        <>
          <Button variant="outline" disabled={process.isPending} onClick={() => setRetryOpen(true)}>
            {i18n._({ id: 'changeRequest.triage.retryApply', message: 'Retry apply' })}
          </Button>
          <ConfirmDialog
            open={retryOpen}
            onOpenChange={setRetryOpen}
            title={i18n._({ id: 'changeRequest.confirm.retryTitle', message: 'Retry applying this change?' })}
            description={i18n._({
              id: 'changeRequest.confirm.retryBody',
              message: 'This re-runs the executor for an item whose automatic execution failed.',
            })}
            cancelLabel={i18n._({ id: 'changeRequest.confirm.cancel', message: 'Cancel' })}
            confirmLabel={i18n._({ id: 'changeRequest.confirm.retryConfirm', message: 'Confirm retry' })}
            isPending={process.isPending}
            onConfirm={handleRetryApply}
          />
        </>
      )}
    </div>
  );
}
